<script lang="ts">
	import type { CloudRecording } from '../types/cloud-recordings';
	import { onDestroy, onMount } from 'svelte';
	import { getCloudRecordings } from 'src/services/cam-cloud/recordings';
	import { ButtonV2, Loader, Scroll, Stack, Text } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	import { capFirstChar } from 'src/helpers/utilities';
	import { camera_previews } from 'src/components/Camera/stores/camera_previews';
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import RecordingsListItemV2 from './RecordingsTab/RecordingsListItemV2.svelte';
	import NoResults from './RecordingsTab/NoResults.svelte';

	export let deviceId: string;

	const RECORDINGS_LIMIT = 3;

	let recordings: CloudRecording[] = [];
	let isLoading = true;
	let loadError = false;

	const loadRecordings = async () => {
		loadError = false;
		try {
			const result = await getCloudRecordings(deviceId, { limit: RECORDINGS_LIMIT, offset: 0 });
			recordings = result.items as CloudRecording[];
		} catch (error) {
			console.log('Failed to load recordings', error);
			loadError = true;
		} finally {
			isLoading = false;
		}
	};

	const handleRetry = () => {
		loadError = false;
		loadRecordings();
	};

	const handleSelect = (event: CustomEvent<CloudRecording>) => {
		const recording = event.detail;
		routeToDeviceSettings.setTarget(deviceId, 'camera_recordings', 'view', recording as any);
	};

	onMount(loadRecordings);

	const unsubscribe = camera_previews.onDeviceRecordingChange(deviceId, () => loadRecordings());
	onDestroy(unsubscribe);
</script>

<div class="mt-l mb-m">
	<Text element="p" styleType="label-large" class="mb-s text-uppercase text-color-muted text-center">
		{$format('last_recordings_title', { default: 'Last Recordings' })}
	</Text>

	{#if isLoading}
		<div class="d-flex justify-content-center width-full p-m">
			<Loader />
		</div>
	{:else if recordings.length > 0}
		<Scroll smallerScroll>
			<Stack gap="s">
				{#each recordings as recording (recording.id)}
					<RecordingsListItemV2
						{recording}
						selected={false}
						variant="simple"
						on:select={handleSelect}
					/>
				{/each}
			</Stack>
		</Scroll>
	{:else}
		<div class="pb-l">
			<NoResults
				noResultsFound={loadError
					? {
							icon: 'fa-light fa-triangle-exclamation',
							title: $format('recordings_list_load_error', {
								default: "Couldn't load recordings",
							}),
							description: $format('recordings_list_load_error_description', {
								default: 'Something went wrong. Please try again.',
							}),
						}
					: undefined}
			/>
			{#if loadError}
				<div class="d-flex justify-content-center mt-m">
					<ButtonV2 variant="secondary" size="m" on:click={handleRetry}>
						{capFirstChar($format('button_try_again', { default: 'Try again' }))}
					</ButtonV2>
				</div>
			{/if}
		</div>
	{/if}
</div>
